import { useState, useRef, useEffect, FormEvent, useCallback, type ComponentPropsWithoutRef } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '@/lib/auth';
import AppLayout from '@/components/AppLayout';
import '@/styles/chat.css';

const markdownComponents = {
  table: ({ children, ...props }: ComponentPropsWithoutRef<'table'>) => (
    <div className="prose-table-wrap">
      <table {...props}>{children}</table>
    </div>
  ),
};

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ConversationSummary {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export default function ChatPage() {
  const { user, refresh } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [activeConversationTitle, setActiveConversationTitle] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const isInternalUser = user?.email?.toLowerCase().endsWith('@zimamedia.com');
  const hasActiveSubscription = isInternalUser ||
    (user?.plan && user.plan !== 'starter' && user.plan !== 'waitlist' &&
     (!user.subscription_status || ['active', 'trialing'].includes(user.subscription_status)));
  const subscriptionBlocked = !hasActiveSubscription;

  const scrollToBottom = () => {
    chatContainerRef.current?.scrollTo({
      top: chatContainerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const loadConversations = useCallback(async () => {
    try {
      const res = await fetch('/api/conversations');
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      }
    } catch {}
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const handleResumeConversation = async (convoId: number) => {
    try {
      const res = await fetch(`/api/conversations/${convoId}/resume`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        const convo = data.conversation;
        setActiveConversationId(convo.id);
        setActiveConversationTitle(convo.title || null);
        setMessages(
          (convo.messages || [])
            .filter((m: { role: string }) => m.role === 'user' || m.role === 'model')
            .map((m: { role: string; content: string }) => ({
              role: m.role === 'user' ? 'user' as const : 'assistant' as const,
              content: m.content,
            }))
        );
        setSidebarOpen(false);
      }
    } catch {}
  };

  const handleDeleteConversation = async (convoId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const res = await fetch(`/api/conversations/${convoId}`, { method: 'DELETE' });
      if (res.ok) {
        setConversations((prev) => prev.filter((c) => c.id !== convoId));
        if (activeConversationId === convoId) {
          setActiveConversationId(null);
          setMessages([]);
        }
      }
    } catch {}
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const message = input.trim();
    if (!message || isLoading) return;

    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setMessages((prev) => [...prev, { role: 'user', content: message }]);
    setIsLoading(true);

    try {
      const body: Record<string, unknown> = { message };
      if (activeConversationId) {
        body.conversation_id = activeConversationId;
      }
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.error) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: `Error: ${data.error}` },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: data.response },
        ]);
        if (data.conversation_id) {
          setActiveConversationId(data.conversation_id);
        }
        if (data.conversation_title) {
          setActiveConversationTitle(data.conversation_title);
        }
        loadConversations();
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Failed to connect to the server. Please try again.' },
      ]);
    } finally {
      setIsLoading(false);
      textareaRef.current?.focus();
      refresh();
    }
  };

  const handleReset = async () => {
    try {
      await fetch('/api/reset', { method: 'POST' });
    } catch {}
    setMessages([]);
    setActiveConversationId(null);
    setActiveConversationTitle(null);
    loadConversations();
  };

  const handleSuggestion = (msg: string) => {
    setInput(msg);
    setTimeout(() => {
      const form = document.getElementById('chat-form') as HTMLFormElement;
      form?.requestSubmit();
    }, 0);
  };

  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !isLoading) {
        const form = document.getElementById('chat-form') as HTMLFormElement;
        form?.requestSubmit();
      }
    }
  };

  const suggestions = [
    { label: 'Account overview', msg: 'Give me a quick performance overview of this account for the last 30 days' },
    { label: 'Show all campaigns', msg: 'Show me all campaigns and their status' },
    { label: 'Top ad groups', msg: 'What are my top performing ad groups this month?' },
    { label: 'Keyword performance', msg: 'Show me keyword performance for the last 7 days' },
  ];

  const firstName = user?.name?.split(' ')[0];

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <AppLayout>
      {sidebarOpen && (
        <div className="chat-sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}
      <div className={`chat-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="chat-sidebar-header">
          <span className="chat-sidebar-title">Conversations</span>
          <button className="chat-sidebar-close" onClick={() => setSidebarOpen(false)}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="chat-sidebar-list">
          {conversations.length === 0 ? (
            <div className="chat-sidebar-empty">No conversations yet</div>
          ) : (
            conversations.map((c) => (
              <div
                key={c.id}
                className={`chat-sidebar-item ${activeConversationId === c.id ? 'active' : ''}`}
                onClick={() => handleResumeConversation(c.id)}
              >
                <div className="chat-sidebar-item-content">
                  <div className="chat-sidebar-item-title">{c.title}</div>
                  <div className="chat-sidebar-item-meta">
                    {c.message_count} messages · {formatDate(c.updated_at)}
                  </div>
                </div>
                <button
                  className="chat-sidebar-item-delete"
                  onClick={(e) => handleDeleteConversation(c.id, e)}
                  title="Delete conversation"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {activeConversationTitle && messages.length > 0 && (
        <div className="chat-active-title">
          <span>{activeConversationTitle}</span>
        </div>
      )}

      <div ref={chatContainerRef} className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-welcome">
            {!user?.mcp_enabled ? (
              <>
                <div className="chat-welcome-icon">
                  <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                </div>
                <h2>Welcome, {firstName}</h2>
                <p>
                  {!user?.google_ads_connected
                    ? 'Link your Google Ads account to get started with AI-powered campaign analysis.'
                    : 'Select a default customer account to start chatting with your Google Ads data.'}
                </p>
                <Link to="/account" className="btn btn-primary" style={{ marginTop: 8 }}>
                  {!user?.google_ads_connected ? 'Link Google Ads' : 'Select Customer Account'}
                </Link>
              </>
            ) : (
              <>
                <div className="chat-welcome-icon">
                  <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                    <line x1="9" y1="9" x2="9.01" y2="9"></line>
                    <line x1="15" y1="9" x2="15.01" y2="9"></line>
                  </svg>
                </div>
                <h2>Welcome, {firstName}</h2>
                <div className="chat-account-badge">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <span>{user?.default_customer_name || 'Account'} &middot; {user?.default_customer_id}</span>
                </div>
                <p>Ask me anything about your Google Ads data. Try one of these:</p>
                <div className="chat-suggestions">
                  {suggestions.map((s) => (
                    <button
                      key={s.label}
                      onClick={() => handleSuggestion(s.msg)}
                      className="chat-suggestion-btn"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="chat-messages-list">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`chat-message${msg.role === 'user' ? ' user' : ''}`}
              >
                <div className={`chat-avatar${msg.role === 'user' ? ' user-avatar' : ' ai-avatar'}`}>
                  {msg.role === 'user' ? 'U' : 'AI'}
                </div>
                <div className={`chat-bubble${msg.role === 'user' ? ' user-bubble' : ' ai-bubble'}`}>
                  {msg.role === 'assistant' ? (
                    <div className="prose">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{msg.content}</ReactMarkdown>
                    </div>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="chat-message animate-fadeIn">
                <div className="chat-avatar ai-avatar">AI</div>
                <div className="chat-bubble ai-bubble">
                  <div className="chat-loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div className="chat-composer">
        {subscriptionBlocked ? (
          <div className="chat-upgrade-prompt">
            <div className="chat-upgrade-icon">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
            </div>
            <p className="chat-upgrade-title">Subscription Required</p>
            <p className="chat-upgrade-text">Subscribe to Starter or Agency to access AI-powered Google Ads management.</p>
            <Link to="/pricing" className="btn btn-primary">View Plans</Link>
          </div>
        ) : (
          <>
            <div className="chat-actions">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="chat-history-btn"
                title="Conversation history"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                History
                {conversations.length > 0 && (
                  <span className="chat-history-count">{conversations.length}</span>
                )}
              </button>
              <button onClick={handleReset} className="chat-reset-btn">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                New Chat
              </button>
            </div>
            <form id="chat-form" onSubmit={handleSubmit}>
              <div className="chat-input-wrap">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={handleTextareaInput}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about your Google Ads data..."
                  rows={1}
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  className="chat-send-btn"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </AppLayout>
  );
}
